import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, Loader2, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FormIdentity, TextSize } from '@/api/reviews';
import { downloadBlob, qrPngBlob } from './qr';
import { TextStyleGear, type TextStyle } from './TextStyleGear';
import { PRINT_SHOP_SEARCH } from './QrHowto';

/** Одна модалка на картинку и на печать (Алексей 18.09, AI57: «давай соединим
 *  модалку печать и скачать»).
 *
 *  Раньше их было две, и параметры в них разъезжались: в печати была подпись и
 *  размер, в скачивании — только фон. Теперь наверху один выбор «Один QR-код /
 *  A4 лист», всё остальное общее, а внизу обе кнопки — скачать и печать.
 *  Прозрачного фона здесь нет: владелец убрал опцию (18.09, AI69) — на
 *  печати он всё равно белый, а в превью только пугал шахматкой.
 */

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 8;
const PAD = 4;       // поле вокруг кода внутри ячейки, мм

type Mode = 'single' | 'sheet';

const SIZES: { cm: number; label: string }[] = [
  { cm: 4, label: '4 см — для стола' },
  { cm: 5, label: '5 см — универсально' },
  { cm: 7, label: '7 см — для кассы' },
  { cm: 10, label: '10 см — на стену' },
];

/** Ячейка = картинка (код с подписью внутри) плюс поля. */
function grid(sizeCm: number, tileAspect: number) {
  const w = sizeCm * 10 + PAD * 2;
  const h = sizeCm * 10 * tileAspect + PAD * 2;
  return {
    cols: Math.max(1, Math.floor((PAGE_W - MARGIN * 2) / w)),
    rows: Math.max(1, Math.floor((PAGE_H - MARGIN * 2) / h)),
    w,
    h,
  };
}

/** Подпись рисуется В САМУ КАРТИНКУ, а не рядом с ней.
 *
 *  Так печать и скачивание дают один и тот же результат, а подпись не может
 *  вылезти за пунктир: кегль подбирается замером (Алексей 18.09, AI62 — «на
 *  печати текст всё ещё не влезает в рамки пунктирной рамки, а на скачать одну
 *  картинку она скачивается без текста под ней»). Раньше подпись жила в HTML
 *  печати, кегль считался прикидкой по длине строки, а в PNG не попадала вовсе.
 */
const CAPTION_SCALE: Record<TextSize, number> = { sm: 0.75, md: 1, lg: 1.3, xl: 1.6 };

function captionFont(px: number, style: TextStyle, identity: FormIdentity) {
  const weight = style.weight === 'bold' ? '600 ' : '';
  return identity === 'surf'
    ? `${weight}${px}px Georgia, 'Times New Roman', serif`
    : `${weight}${px}px system-ui, sans-serif`;
}

/** Автокапс — только у Surf и только пока не выключен (AI63). */
function captionText(text: string, style: TextStyle, identity: FormIdentity) {
  return identity === 'surf' && (style.caps ?? true) ? text.toUpperCase() : text;
}

/** Разбивка на строки по ширине; null — не влезает в maxLines. */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] | null {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width <= maxWidth || !cur) {
      cur = next;
    } else {
      lines.push(cur);
      cur = w;
    }
    if (lines.length > maxLines) return null;
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) return null;
  return lines.every((l) => ctx.measureText(l).width <= maxWidth) ? lines : null;
}

/** Код с подписью одной картинкой; ширина = codePx. */
function composeTile(
  qr: HTMLImageElement,
  codePx: number,
  caption: string,
  style: TextStyle,
  identity: FormIdentity,
  background: 'white' | 'transparent',
): HTMLCanvasElement {
  const text = caption ? captionText(caption, style, identity) : '';
  const probe = document.createElement('canvas').getContext('2d')!;
  const maxWidth = codePx * 0.96;
  let px = Math.round(codePx * 0.075 * CAPTION_SCALE[style.size]);
  let lines: string[] = [];
  if (text) {
    const floor = Math.max(8, Math.round(codePx * 0.028));
    for (; px >= floor; px = Math.round(px * 0.92)) {
      probe.font = captionFont(px, style, identity);
      const got = wrapLines(probe, text, maxWidth, 2);
      if (got) { lines = got; break; }
    }
    if (!lines.length) {
      px = floor;
      probe.font = captionFont(px, style, identity);
      lines = [text];
    }
  }
  const lineH = Math.round(px * 1.2);
  const pad = text ? Math.round(px * 0.55) : 0;
  const canvas = document.createElement('canvas');
  canvas.width = codePx;
  canvas.height = codePx + (text ? pad + lineH * lines.length + pad : 0);
  const ctx = canvas.getContext('2d')!;
  if (background === 'white') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(qr, 0, 0, codePx, codePx);
  if (text) {
    ctx.fillStyle = '#4b5563';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = captionFont(px, style, identity);
    // maxWidth у fillText — страховка на случай, когда подбор размера сдался
    // (одно слово длиннее строки): браузер сожмёт букву по ширине, а не
    // выпустит подпись за пунктирную рамку листа (Алексей 18.09, AI62).
    lines.forEach((l, i) => ctx.fillText(l, codePx / 2, codePx + pad + i * lineH, maxWidth));
  }
  return canvas;
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
}

/** Печатаем готовую плитку: HTML только мостит её по листу и рисует пунктир. */
function printHtml(src: string, tileAspect: number, sizeCm: number) {
  const g = grid(sizeCm, tileAspect);
  const cell = `<div class="c"><img src="${src}" alt=""></div>`;
  return '<!doctype html><html><head><meta charset="utf-8">' +
    `<style>@page{size:A4;margin:${MARGIN}mm}html,body{margin:0;padding:0}` +
    `.g{display:grid;grid-template-columns:repeat(${g.cols},${g.w}mm);grid-auto-rows:${g.h}mm;` +
    'width:max-content;margin:0 auto;border-top:0.3mm dashed #9ca3af;border-left:0.3mm dashed #9ca3af}' +
    '.c{box-sizing:border-box;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
    'border-right:0.3mm dashed #9ca3af;border-bottom:0.3mm dashed #9ca3af;break-inside:avoid}' +
    `img{width:${sizeCm}cm;height:${(sizeCm * tileAspect).toFixed(3)}cm;display:block}` +
    '</style></head><body>' +
    `<div class="g">${cell.repeat(g.cols * g.rows)}</div></body></html>`;
}

function printViaIframe(html: string): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    const cleanup = () => { window.setTimeout(() => iframe.remove(), 1000); };
    iframe.onload = async () => {
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument;
      if (!win || !doc) { cleanup(); resolve(); return; }
      await Promise.all([...doc.images].map((img) => img.decode().catch(() => undefined)));
      win.addEventListener('afterprint', cleanup, { once: true });
      win.focus();
      win.print();
      // Safari не всегда шлёт afterprint — подчищаем с запасом.
      window.setTimeout(cleanup, 60_000);
      resolve();
    };
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

/** Лист A4 картинкой — ровно то же, что уйдёт в печать (300 dpi). */
async function sheetBlob(tileSrc: string, tileAspect: number, sizeCm: number): Promise<Blob> {
  const dpi = 300;
  const px = (mm: number) => Math.round((mm / 25.4) * dpi);
  const g = grid(sizeCm, tileAspect);
  const canvas = document.createElement('canvas');
  canvas.width = px(PAGE_W);
  canvas.height = px(PAGE_H);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const img = await loadImage(tileSrc);
  const cw = px(g.w);
  const ch = px(g.h);
  const left = Math.round((canvas.width - cw * g.cols) / 2);
  const top = px(MARGIN);
  ctx.strokeStyle = '#9ca3af';
  ctx.lineWidth = Math.max(1, px(0.3));
  ctx.setLineDash([px(1.5), px(1.5)]);
  const codePx = px(sizeCm * 10);
  for (let r = 0; r < g.rows; r++) {
    for (let c = 0; c < g.cols; c++) {
      const x = left + c * cw;
      const y = top + r * ch;
      ctx.strokeRect(x, y, cw, ch);
      ctx.drawImage(img, x + (cw - codePx) / 2, y + px(PAD), codePx, codePx * tileAspect);
    }
  }
  return canvasBlob(canvas);
}

export function QrImageDialog({ open, onOpenChange, url, logoSrc, identity, defaultCaption }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  /** null — код без логотипа. */
  logoSrc: string | null;
  identity: FormIdentity;
  defaultCaption: string;
}) {
  const [mode, setMode] = useState<Mode>('single');
  const [sizeCm, setSizeCm] = useState(5);
  const [withCaption, setWithCaption] = useState(true);
  // Подпись и её стиль правятся прямо здесь (Алексей 18.09, AI57/AI63).
  const [caption, setCaption] = useState(defaultCaption);
  const [style, setStyle] = useState<TextStyle>({ size: 'md', weight: 'normal', caps: true });
  const [qr, setQr] = useState<HTMLImageElement | null>(null);
  const [tile, setTile] = useState<{ src: string; aspect: number } | null>(null);
  const [busy, setBusy] = useState<'print' | 'download' | null>(null);
  const captionRef = useRef(defaultCaption);

  useEffect(() => {
    if (captionRef.current !== defaultCaption) {
      captionRef.current = defaultCaption;
      setCaption(defaultCaption);
    }
  }, [defaultCaption]);

  // Сам код: рисуем один раз на открытие. 1536 px — этот же файл уходит в печать
  // и в лист A4 (300 dpi), при 10 см коду нужно ~1180 px, иначе тянем вверх.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    let objectUrl: string | null = null;
    void qrPngBlob(url, { background: 'white', logoSrc, size: 1536 })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        return loadImage(objectUrl);
      })
      .then((img) => { if (alive) setQr(img); });
    return () => {
      alive = false;
      if (objectUrl) { const u = objectUrl; window.setTimeout(() => URL.revokeObjectURL(u), 90_000); }
      setQr(null);
    };
  }, [open, url, logoSrc]);

  const text = withCaption ? caption.trim() : '';

  // Плитка «код + подпись» — одна на превью, печать и лист.
  useEffect(() => {
    if (!qr) { setTile(null); return; }
    const canvas = composeTile(qr, 1536, text, style, identity, 'white');
    const aspect = canvas.height / canvas.width;
    let url2: string | null = null;
    let alive = true;
    void canvasBlob(canvas).then((b) => {
      url2 = URL.createObjectURL(b);
      if (alive) setTile({ src: url2, aspect });
    });
    return () => {
      alive = false;
      if (url2) { const u = url2; window.setTimeout(() => URL.revokeObjectURL(u), 90_000); }
    };
  }, [qr, text, style, identity]);

  const g = useMemo(() => grid(sizeCm, tile?.aspect ?? 1), [sizeCm, tile]);
  const count = mode === 'sheet' ? g.cols * g.rows : 1;
  const fileBase = (caption || 'qr').replace(/[\\/:*?"<>|]+/g, ' ').trim();

  async function print() {
    if (!tile) return;
    setBusy('print');
    try {
      await printViaIframe(printHtml(tile.src, tile.aspect, sizeCm));
    } finally {
      setBusy(null);
    }
  }

  async function download() {
    if (!tile) return;
    setBusy('download');
    try {
      if (mode === 'sheet') {
        downloadBlob(await sheetBlob(tile.src, tile.aspect, sizeCm), `${fileBase} — лист A4.png`);
      } else {
        // Отдельный прогон: у одиночного кода бывает прозрачный фон, и подпись
        // должна лечь на него же, а не на белый.
        const blob = await qrPngBlob(url, { background: 'white', logoSrc, size: 2048 });
        const objectUrl = URL.createObjectURL(blob);
        try {
          const img = await loadImage(objectUrl);
          const canvas = composeTile(img, 2048, text, style, identity, 'white');
          downloadBlob(await canvasBlob(canvas), `${fileBase} — QR.png`);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }
    } finally {
      setBusy(null);
    }
  }

  const [copied, setCopied] = useState(false);
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер закрыт политикой браузера — ссылку всегда можно взять из формы.
    }
  };

  // Превью листа: 1 мм = PREVIEW px.
  const PREVIEW = 0.9;
  const mm = (v: number) => `${v * PREVIEW}px`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} widthClassName="sm:max-w-3xl">
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
        {/* Подпись под заголовком убрана (Алексей 21.09, AI99): она пересказывала
            то, что и так видно в переключателе и на превью. */}
        <DialogHeader>
          <DialogTitle>Наклейки с QR-кодом</DialogTitle>
        </DialogHeader>

        {/* Настройки в одной карточке с разделителями, превью — отдельным
            блоком: вразброс, каждая со своим отступом, они «лепились» и
            выглядели пугающе (Алексей 18.09, AI69). */}
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="min-w-0 flex-1 space-y-4 text-sm">
            <div className="divide-y divide-border rounded-xl border border-border">
              <div className="space-y-2 p-3">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Что нужно</div>
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/60 p-1">
                  {([['single', 'Один QR-код'], ['sheet', 'A4 лист']] as [Mode, string][]).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setMode(k)}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-sm transition-colors',
                        mode === k
                          ? 'bg-background font-medium shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2 p-3">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Размер кода</div>
                {/* Был системный дропдаун — единственный на всё окно, и выглядел
                    чужим (Алексей 21.09, AI98). Теперь те же сегменты, что и у
                    «Что нужно» сразу над ним. */}
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/60 p-1">
                  {SIZES.map((s) => (
                    <button
                      key={s.cm}
                      type="button"
                      onClick={() => setSizeCm(s.cm)}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-sm transition-colors',
                        sizeCm === s.cm
                          ? 'bg-background font-medium shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2 p-3">
                <label className="flex cursor-pointer items-center justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Подпись под кодом
                  </span>
                  <input
                    type="checkbox"
                    checked={withCaption}
                    onChange={(e) => setWithCaption(e.target.checked)}
                    className="size-4 accent-[var(--color-primary)]"
                  />
                </label>
                {withCaption && (
                  <div className="flex items-center gap-2">
                    <input
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Например, Surf Coffee Остоженка"
                      className="h-9 min-w-0 flex-1 rounded-lg border border-border bg-background px-2"
                    />
                    {/* Та же шестерёнка, что у текстов формы (Алексей 18.09, AI63). */}
                    <TextStyleGear value={style} onChange={(p) => setStyle((v) => ({ ...v, ...p }))} />
                  </div>
                )}
              </div>
            </div>

            {/* Кнопка стояла ВНУТРИ абзаца, и её высота раздвигала строки вокруг
                (Алексей 18.09, AI69). Текст и действия теперь разведены. */}
            <div className="rounded-lg border border-dashed border-border p-3">
              <div className="text-xs font-medium">Нужно другое оформление?</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Соберите код на ссылку формы в любом стороннем генераторе.
              </p>
              {/* Кнопка одна: перечислять, что там меняется, и вести за руку в
                  конкретный генератор владелец просил перестать (AI101). */}
              <div className="mt-2.5">
                <Button size="sm" variant="outline" onClick={() => void copyUrl()}>
                  {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  {copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center justify-center gap-2">
          {mode === 'sheet' ? (
            <div
              className="relative mx-auto shrink-0 rounded-sm border border-border bg-white shadow-sm"
              style={{ width: mm(PAGE_W), height: mm(PAGE_H) }}
              aria-label="Превью листа"
            >
              <div
                className="absolute left-1/2 grid -translate-x-1/2 border-l border-t border-dashed border-gray-400"
                style={{ top: mm(MARGIN), gridTemplateColumns: `repeat(${g.cols}, ${mm(g.w)})`, gridAutoRows: mm(g.h) }}
              >
                {Array.from({ length: count }, (_, i) => (
                  <div key={i} className="flex items-center justify-center border-b border-r border-dashed border-gray-400">
                    {tile
                      ? <img src={tile.src} alt="" style={{ width: mm(sizeCm * 10) }} />
                      : <Loader2 className="size-3 animate-spin text-gray-400" />}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex shrink-0 flex-col items-center gap-2">
              <div className="rounded-lg border border-border bg-white p-3">
                {tile
                  ? <img src={tile.src} alt="QR-код" className="w-44" />
                  : <div className="flex size-44 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>}
              </div>
            </div>
          )}
            <p className="text-xs text-muted-foreground">
              {mode === 'sheet' ? (count === 1 ? '1 код на листе' : `${count} кодов на листе`) : 'PNG 2048 px'}
            </p>
          </div>
        </div>

        <DialogFooter>
          {/* Куда нести файл — на одной строке с кнопками, по нижней границе
              (Алексей 21.09, AI100): в колонке настроек этот абзац висел
              посреди окна, далеко от «Печати». */}
          <p className="mr-auto self-center text-xs leading-relaxed text-muted-foreground">
            Печатать можно на обычном принтере или{' '}
            <a href={PRINT_SHOP_SEARCH} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              в типографии рядом
            </a>
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Закрыть</Button>
          <Button variant="outline" onClick={() => void download()} disabled={!tile || busy !== null}>
            {busy === 'download' ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} Скачать
          </Button>
          <Button onClick={() => void print()} disabled={!tile || busy !== null}>
            {busy === 'print' ? <Loader2 className="size-4 animate-spin" /> : <Printer className="size-4" />} Печать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
